import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    CButton, CCard, CCardBody, CCardHeader, COffcanvas, COffcanvasBody, COffcanvasHeader, COffcanvasTitle
} from '@coreui/react';
import CIcon from "@coreui/icons-react";
import { cilPencil, cilTrash, cilX } from "@coreui/icons";

import { apiClient } from 'services/Api';
import { jwtDecode } from 'jwt-decode';
import DataTableComponent from "components/DataTableComponent";
import AlertNotification from 'components/globals/AlertNotification';
import TextFieldCell from 'components/TextFieldCell';

const ImprovementOppList = () => {
    const navigate = useNavigate();
    const [improvementOpps, setImprovementOpps] = useState([]);
    const location = useLocation();
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [availableYears, setAvailableYears] = useState([]);
    const [previewImprovementOpp, setPreviewImprovementOpp] = useState(null);

    const token = localStorage.getItem("access_token");
    const decodedToken = jwtDecode(token);
    const is_director = decodedToken.is_director;

    useEffect(() => {
        const fetchImprovementOpps = async (year = selectedYear) => {
            try {
                const response = await apiClient.get(`/improvement-opportunities/?year=${year}`);
                setImprovementOpps(response.data.improvemente_opps);
                setPreviewImprovementOpp((current) =>
                    current
                        ? response.data.improvemente_opps.find((improvementOpp) => improvementOpp.id === current.id) || null
                        : null,
                );
                setAvailableYears(response.data.available_years);
            } catch (error) {
                console.error('Error fetching improvement-opportunities:', error);
            }
        };

        fetchImprovementOpps(selectedYear);
    }, [location.pathname, selectedYear]);

    const handleDelete = async (improvementOppId) => {
        try {
            await apiClient.delete(`/improvement-opportunities/${improvementOppId}/`);
            setImprovementOpps((current) => current.filter((improvementOpp) => improvementOpp.id !== improvementOppId));
            setPreviewImprovementOpp((current) => current?.id === improvementOppId ? null : current);
        } catch (error) {
            console.error('Error deleting improvement opp:', error);
        }
    };

    const columns = [
        { name: "Código", selector: (row) => row.code, sortable: true },
        { name: "Descrição", cell: (row) => <TextFieldCell html={row.description} onClick={() => setPreviewImprovementOpp(row)} />, sortable: true, grow: 5 },
        { name: "Seguimento", selector: (row) => row.action ? "Sim" : row.action === false ? "Não" : "", sortable: true },
    ];

    return (
        <>
            <AlertNotification />
            <CCard>
                <CCardHeader className="d-flex justify-content-between align-items-center">
                    <h3 className="mb-0">Oportunidades de Melhoria</h3>
                </CCardHeader>
                <CCardBody>
                    <DataTableComponent
                        columns={columns}
                        data={improvementOpps}
                        years={availableYears}  // Optional
                        selectedYear={selectedYear}  // Optional
                        onYearChange={(newYear) => setSelectedYear(newYear)}  // Optional
                        onRowClicked={setPreviewImprovementOpp}
                    />
                </CCardBody>
            </CCard>

            {previewImprovementOpp && (
                <div
                    style={{ position: "fixed", inset: 0, background: "transparent", zIndex: 1040 }}
                    onClick={() => setPreviewImprovementOpp(null)}
                    aria-hidden="true"
                />
            )}

            <COffcanvas
                placement="end"
                visible={Boolean(previewImprovementOpp)}
                onHide={() => setPreviewImprovementOpp(null)}
                backdrop={false}
                scrollable
                scroll
                style={{ width: "560px", maxWidth: "95vw" }}
            >
                <COffcanvasHeader className="border-bottom">
                    <COffcanvasTitle className="d-flex align-items-center me-auto">
                        <div>
                            <div className="fw-semibold">Oportunidade de Melhoria</div>
                            <div className="text-medium-emphasis small">{previewImprovementOpp?.code || "-"}</div>
                        </div>
                    </COffcanvasTitle>
                    <div className="d-flex align-items-center gap-2">
                        {previewImprovementOpp && (
                            <CButton
                                color="info"
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/oportunidades-melhoria/${previewImprovementOpp.id}`)}
                            >
                                <CIcon icon={cilPencil} className="me-1" />
                                {previewImprovementOpp.action == null && is_director ? "Editar" : "Ver"}
                            </CButton>
                        )}
                        {previewImprovementOpp?.action === null && is_director && (
                            <CButton
                                color="danger"
                                size="sm"
                                className="text-white"
                                onClick={() => {
                                    if (window.confirm("Tem a certeza que quer eliminar este registo?")) {
                                        handleDelete(previewImprovementOpp.id);
                                    }
                                }}
                            >
                                <CIcon icon={cilTrash} className="me-1" />
                                Eliminar
                            </CButton>
                        )}
                        <CButton
                            color="link"
                            className="text-body p-0"
                            onClick={() => setPreviewImprovementOpp(null)}
                            title="Fechar"
                        >
                            <CIcon icon={cilX} />
                        </CButton>
                    </div>
                </COffcanvasHeader>
                <COffcanvasBody>
                    {previewImprovementOpp && (
                        <div className="d-flex flex-column gap-3">
                            <div className="border rounded-3 p-3 bg-body-tertiary">
                                <div className="text-uppercase text-medium-emphasis small">Código</div>
                                <div className="fw-semibold">{previewImprovementOpp.code || "-"}</div>
                            </div>
                            <div className="border rounded-3 p-3 bg-body-tertiary">
                                <div className="text-uppercase text-medium-emphasis small">Descrição</div>
                                <TextFieldCell html={previewImprovementOpp.description || "-"} />
                            </div>
                            <div className="border rounded-3 p-3 bg-body-tertiary">
                                <div className="text-uppercase text-medium-emphasis small">Seguimento</div>
                                <div className="fw-semibold">
                                    {previewImprovementOpp.action ? "Sim" : previewImprovementOpp.action === false ? "Não" : "-"}
                                </div>
                            </div>
                        </div>
                    )}
                </COffcanvasBody>
            </COffcanvas>
        </>
    );

};

export default ImprovementOppList;
